import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Menu, X } from "lucide-react";
import { Logo } from "./Logo";

export const WHATSAPP_URL =
  "https://wa.me/5511951445379?text=Ol%C3%A1!%20Quero%20saber%20mais%20sobre%20o%20lan%C3%A7amento%20do%20O%20Rei%20da%2025.";

const MENU_CATEGORIES = [
  "Bolsas",
  "Perfumes",
  "Acessórios para Celular",
  "Utensílios Domésticos",
  "Ferramentas",
  "Maquiagens",
  "Relógios",
  "Itens para Festa",
  "Personalização & Sublimação",
];

const MenuOverlay = ({ open, onClose }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        data-testid="menu-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        className="fixed inset-0 z-[80] flex flex-col bg-ink/95 backdrop-blur-xl"
      >
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
          <Logo compact />
          <button
            onClick={onClose}
            data-testid="menu-close-button"
            aria-label="Fechar menu"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/40 text-gold transition-colors duration-300 hover:border-royal hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-5 pb-16 sm:px-8">
          <p className="mb-8 text-[11px] font-semibold uppercase tracking-[0.5em] text-royal">
            Categorias — em breve
          </p>
          <ul className="grid grid-cols-1 gap-x-16 sm:grid-cols-2">
            {MENU_CATEGORIES.map((category, i) => (
              <motion.li
                key={category}
                data-testid={`menu-item-${i}`}
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.08 * i, ease: [0.85, 0, 0.15, 1] }}
                className="group flex cursor-default items-baseline gap-4 border-b border-white/10 py-4"
              >
                <span className="font-sans text-xs font-bold tracking-[0.3em] text-gold/60">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-serif text-2xl text-neutral-300 transition-colors duration-300 group-hover:text-gold sm:text-3xl">
                  {category}
                </span>
              </motion.li>
            ))}
          </ul>
        </nav>
      </motion.div>
    )}
  </AnimatePresence>
);

export const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <>
      <motion.header
        data-testid="site-header"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.2, ease: [0.85, 0, 0.15, 1] }}
        className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-black/60 backdrop-blur-xl"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
          <div className="flex items-center gap-5">
            <button
              onClick={() => setMenuOpen(true)}
              data-testid="menu-open-button"
              aria-label="Abrir menu"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-neutral-300 transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              <Menu className="h-5 w-5" />
            </button>
            <Logo compact />
          </div>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-button"
            className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full border border-gold/40 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-gold transition-colors duration-500 hover:border-royal hover:text-white"
          >
            <span className="absolute inset-0 -translate-x-full bg-royal transition-transform duration-500 ease-out group-hover:translate-x-0" />
            <MessageCircle className="relative z-10 h-4 w-4" />
            <span className="relative z-10 hidden sm:inline">WhatsApp</span>
          </a>
        </div>
      </motion.header>
      <MenuOverlay open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
};
